import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import YouTube from "react-youtube";
import "../styles/VideoPlayer.css";

// The environment variable for YouTube Data API key
const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

const ArticlePlayer = () => {
  const { videoId } = useParams(); // e.g. "Ra7aQlenTb8"
  const navigate = useNavigate();
  const location = useLocation();

  // Track if we're on mobile or desktop, for separate layouts
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // We’ll fetch the article from Craft CMS
  // and also fetch the YouTube snippet to get the video title & duration
  const [isMinimized, setIsMinimized] = useState(false);
  const [articleText, setArticleText] = useState("Fetching article...");
  const [videoTitle, setVideoTitle] = useState("Loading...");
  const [videoDuration, setVideoDuration] = useState("0 min");

  // For showing the "Up Next" if we have a playlist in the query (e.g. ?list=xxxx)
  const [isFromPlaylist, setIsFromPlaylist] = useState(false);
  const [playlistItems, setPlaylistItems] = useState([]);

  // For the share modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedToastVisible, setCopiedToastVisible] = useState(false);

  // Extract a possible playlist ID from location.search
  const urlParams = new URLSearchParams(location.search);
  const playlistId = urlParams.get("list");
  const [areControlsVisible, setAreControlsVisible] = useState(true);

  useEffect(() => {
    // 1) If there's a localStorage minimized video, see if we need to respect that
    const minimizedVideo = localStorage.getItem("minimizedVideo");
    if (minimizedVideo) {
      setIsMinimized(true);
    }

    // 2) Fetch from Craft CMS to find the article’s `mainparagraph`
    const fetchCraftData = async () => {
      try {
        const cmsResponse = await fetch(
          "https://www.cmsnovatide.com/dashboard-content.json"
        );
        const cmsData = await cmsResponse.json();

        // Find the entry titled "NexaVista YouTube Videos"
        const nexaVistaEntry = cmsData?.data?.find(
          (entry) => entry.title === "NexaVista YouTube Videos"
        );

        if (!nexaVistaEntry || !Array.isArray(nexaVistaEntry.articlesList)) {
          console.warn("articlesList not found in the CMS response.");
          return;
        }

        // We find the article whose URL contains this videoId
        // e.g. article.url might be "https://www.youtube.com/watch?v=Ra7aQlenTb8"
        const matchingArticle = nexaVistaEntry.articlesList.find((article) => {
          if (article.url && article.url.includes("watch?v=")) {
            const idInUrl = article.url.split("watch?v=")[1].split("&")[0];
            return idInUrl === videoId;
          }
          return false;
        });

        if (matchingArticle) {
          setArticleText(matchingArticle.mainparagraph || "");
        } else {
          setArticleText("No matching article text found for this video.");
        }
      } catch (error) {
        console.error("Error fetching craft data:", error);
        setArticleText("Error fetching article from CMS.");
      }
    };

    // 3) Fetch from YouTube to get the snippet (title, contentDetails, etc.)
    const fetchVideoDetails = async () => {
      if (!API_KEY) {
        console.warn("Missing REACT_APP_YOUTUBE_API_KEY in environment.");
        return;
      }
      try {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?key=${API_KEY}&part=snippet,contentDetails&id=${videoId}`
        );
        const data = await response.json();

        if (data.items && data.items.length > 0) {
          const videoData = data.items[0];
          setVideoTitle(videoData.snippet.title);
          setVideoDuration(parseDuration(videoData.contentDetails.duration));
        } else {
          setVideoTitle("Video not found on YouTube");
        }
      } catch (error) {
        console.error("Error fetching YouTube details:", error);
      }
    };

    // 4) If there's a playlist in the URL, fetch up to 5 items for "Up Next"
    const fetchPlaylistItems = async () => {
      if (!playlistId) {
        setIsFromPlaylist(false);
        setPlaylistItems([]);
        return;
      }
      setIsFromPlaylist(true);

      try {
        const response = await fetch(
          `https://www.googleapis.com/youtube/v3/playlistItems?key=${API_KEY}&part=snippet,contentDetails&playlistId=${playlistId}&maxResults=5`
        );
        const data = await response.json();

        if (data.items) {
          const videoIds = data.items.map(
            (item) => item.contentDetails.videoId
          );
          // fetch durations for each video
          const durationResponse = await fetch(
            `https://www.googleapis.com/youtube/v3/videos?key=${API_KEY}&part=contentDetails&id=${videoIds.join(
              ","
            )}`
          );
          const durationData = await durationResponse.json();

          const formattedPlaylist = data.items.map((item, index) => ({
            id: item.contentDetails.videoId,
            title: item.snippet.title,
            thumbnail: item.snippet.thumbnails.high.url,
            duration: parseDuration(
              durationData.items[index]?.contentDetails?.duration || "PT0M0S"
            ),
          }));

          setPlaylistItems(formattedPlaylist);
        }
      } catch (error) {
        console.error("Error fetching playlist videos:", error);
      }
    };

    fetchCraftData();
    fetchVideoDetails();
    fetchPlaylistItems();
  }, [videoId, playlistId]);

  // -----------------------
  // Hide footers & navbars
  // -----------------------
  useEffect(() => {
    // Hide footer .footer-content-2
    const footer = document.querySelector(".footer-content-2");
    if (footer) {
      footer.style.display = "none";
    }
    return () => {
      if (footer) {
        footer.style.display = "block";
      }
    };
  }, []);

  useEffect(() => {
    // Hide .mobile-navbar on mount
    const navbar = document.querySelector("nav.mobile-navbar");
    if (navbar) {
      navbar.style.display = "none";
    }
    return () => {
      if (navbar) {
        navbar.style.display = "";
      }
    };
  }, []);

  // Always scroll to top when this component loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [videoId]);

  useEffect(() => {
    let hideTimeout;

    // This function will be called whenever the user moves the mouse
    const handleMouseMove = () => {
      // Show controls
      setAreControlsVisible(true);

      // Clear any old timeout and start a new 3s countdown
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        setAreControlsVisible(false);
      }, 3000); // 3 seconds of inactivity -> hide controls
    };

    // We attach the event to whichever container wraps the YouTube player
    // In your code, that might be .video-widget or .video-widget-desktop
    const container =
      document.querySelector(".video-widget-desktop") ||
      document.querySelector(".video-widget");

    if (container) {
      container.addEventListener("mousemove", handleMouseMove);
    }

    // Cleanup
    return () => {
      if (container) {
        container.removeEventListener("mousemove", handleMouseMove);
      }
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, []);

  const shareLink = `https://www.youtube.com/watch?v=${videoId}`;

  const handleShareClick = () => setShowShareModal(true);
  const handleCloseShareModal = () => setShowShareModal(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopiedToastVisible(true);
      setTimeout(() => {
        setCopiedToastVisible(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy link!", err);
    }
  };

  const handleShareWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=Check this out: ${shareLink}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShareTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=Check this out: ${shareLink}`,
      "_blank"
    );
  };

  const handleClose = () => {
    localStorage.removeItem("minimizedVideo");
    navigate("/academy");
  };

  // Minimize player to a small corner version
  const handleMinimize = () => {
    const minimizedData = {
      videoId,
      title: videoTitle,
    };
    localStorage.setItem("minimizedVideo", JSON.stringify(minimizedData));
    navigate("/academy");
  };

  // For any "Up Next" item in the playlist
  const handlePlaylistVideoClick = (nextVideoId) => {
    navigate(`/read/${nextVideoId}?list=${playlistId}`);
    // Quick hack to re-trigger everything
    window.location.reload();
  };

  const parseDuration = (isoDuration) => {
    const match = isoDuration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    const hours = match[1] ? parseInt(match[1].replace("H", "")) : 0;
    const minutes = match[2] ? parseInt(match[2].replace("M", "")) : 0;
    const seconds = match[3] ? parseInt(match[3].replace("S", "")) : 0;
    const totalMinutes = Math.round(hours * 60 + minutes + seconds / 60);
    return `${totalMinutes} min`;
  };

  const renderMobile = () => (
    <div className={`video-player-container ${isMinimized ? "minimized" : ""}`}>
      {/* Video Widget */}
      <div className="video-widget">
        <YouTube
          videoId={videoId}
          opts={{
            width: "100%",
            height: isMinimized ? "120" : "360",
          }}
        />
        {areControlsVisible && (
          <div className="controls">
            <button className="minimize-btn" onClick={handleMinimize}>
              <img src="/static/images/minimizeSymbol.png" alt="Minimize" />
            </button>
            <button className="close-btn" onClick={handleClose}>
              <img src="/static/images/closeSymbol.png" alt="Close" />
            </button>
          </div>
        )}
      </div>

      {/* Article Details */}
      {!isMinimized && (
        <div className="video-details">
          <h2>{videoTitle}</h2>
          <p>{videoDuration}</p>

          {/* SHARE */}
          <button className="share-btn" onClick={handleShareClick}>
            <img
              src="/static/images/shareSymbol.png"
              alt="Share"
              className="share-symbol"
            />
            Share
          </button>

          {/* MAIN PARAGRAPH from the article */}
          <p>{articleText}</p>

          {/* "Up Next" if from a playlist */}
          {isFromPlaylist && playlistItems.length > 0 && (
            <div className="up-next-section">
              <h3>Up Next</h3>
              {playlistItems.map((video) => (
                <div
                  key={video.id}
                  className="playlist-video"
                  onClick={() => handlePlaylistVideoClick(video.id)}
                >
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="playlist-thumbnail"
                  />
                  <div className="playlist-video-details">
                    <h4>{video.title}</h4>
                    <p>{video.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* SHARE MODAL */}
          {showShareModal && (
            <div className="share-modal-overlay">
              <div className="share-modal-content">
                <h3>Share on social media</h3>
                <p className="link-to-share">Link to share</p>

                <div className="share-link-box">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="share-link-input"
                  />
                  <button className="copy-button" onClick={handleCopyLink}>
                    <i className="fa fa-clone"></i>
                  </button>
                </div>

                <div className="social-icons">
                  <button onClick={handleShareWhatsApp}>
                    <i className="fa-brands fa-whatsapp"></i>
                  </button>
                  <button onClick={handleShareTwitter}>
                    <i className="fa-brands fa-x-twitter"></i>
                  </button>
                </div>

                <button
                  className="close-share-modal"
                  onClick={handleCloseShareModal}
                >
                  Close
                </button>
              </div>
            </div>
          )}
          {copiedToastVisible && (
            <div className="copied-toast">Link copied!</div>
          )}
        </div>
      )}
    </div>
  );

  // DESKTOP RENDER
  const renderDesktop = () => (
    <div
      className={`video-player-container-desktop ${
        isMinimized ? "minimized" : ""
      }`}
    >
      <div className="video-widget-desktop">
        <YouTube
          videoId={videoId}
          opts={{
            width: "100%",
            height: isMinimized ? "120" : "480",
          }}
        />
        {areControlsVisible && (
          <div className="controls-desktop">
            <button className="minimize-btn" onClick={handleMinimize}>
              <img src="/static/images/minimizeSymbol.png" alt="Minimize" />
            </button>
            <button className="close-btn" onClick={handleClose}>
              <img src="/static/images/closeSymbol.png" alt="Close" />
            </button>
          </div>
        )}
      </div>

      {/* Article Details */}
      {!isMinimized && (
        <div className="video-details">
          <h2>{videoTitle}</h2>
          <p>{videoDuration}</p>

          <button className="share-btn" onClick={handleShareClick}>
            <img
              src="/static/images/shareSymbol.png"
              alt="Share"
              className="share-symbol"
            />
            Share
          </button>

          {/* MAIN PARAGRAPH from the article */}
          <p>{articleText}</p>

          {/* "Up Next" if from a playlist */}
          {isFromPlaylist && playlistItems.length > 0 && (
            <div className="up-next-section">
              <h3>Up Next</h3>
              {playlistItems.map((video) => (
                <div
                  key={video.id}
                  className="playlist-video"
                  onClick={() => handlePlaylistVideoClick(video.id)}
                >
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="playlist-thumbnail"
                  />
                  <div className="playlist-video-details">
                    <h4>{video.title}</h4>
                    <p>{video.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* SHARE MODAL */}
      {showShareModal && (
        <div className="share-modal-overlay">
          <div className="share-modal-content">
            <h3>Share on social media</h3>
            <p className="link-to-share">Link to share</p>

            <div className="share-link-box">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="share-link-input"
              />
              <button className="copy-button" onClick={handleCopyLink}>
                <i className="fa fa-clone"></i>
              </button>
            </div>

            <div className="social-icons">
              <button onClick={handleShareWhatsApp}>
                <i className="fa-brands fa-whatsapp"></i>
              </button>
              <button onClick={handleShareTwitter}>
                <i className="fa-brands fa-x-twitter"></i>
              </button>
            </div>

            <button
              className="close-share-modal"
              onClick={handleCloseShareModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
      {copiedToastVisible && <div className="copied-toast">Link copied!</div>}
    </div>
  );

  // FINAL: choose mobile or desktop
  return isMobile ? renderMobile() : renderDesktop();
};

export default ArticlePlayer;
